import { ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { finalize, TimeoutError, timeout } from 'rxjs';
import { AuthService } from './auth.service';
import { ChangeCredentialsRequest, RegisterRequest, UserRole } from './auth.model';
import { API_BASE_URL } from '../core/api-base-url';

type AuthMode = 'login' | 'register' | 'change-credentials';

@Component({
  selector: 'app-auth',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './auth.view.html',
  styleUrls: ['./auth.view.css']
})
export class AuthComponent implements OnInit, OnDestroy {
  // Defensive timeout guard for slow or stalled network requests.
  private readonly requestTimeoutMs = 15000;
  private loadingGuardTimer: ReturnType<typeof setTimeout> | null = null;
  private destroyed = false;

  mode: AuthMode = 'login';

  form = {
    name: '',
    username: '',
    password: '',
    email: '',
    role: 'owner' as UserRole,
    mechanicPhone: '',
    mechanicLocation: ''
  };

  errorMessage = '';
  successMessage = '';
  loading = false;
  registrationStep: 'account' | 'mechanic-details' = 'account';

  constructor(
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly cdr: ChangeDetectorRef,
    private readonly ngZone: NgZone
  ) {}

  ngOnInit(): void {
    const routeMode = this.route.snapshot.data['mode'] as AuthMode | undefined;
    this.mode = routeMode ?? 'login';
    if (this.mode !== 'register') {
      this.registrationStep = 'account';
    }

    const registered = this.route.snapshot.queryParamMap.get('registered');
    const username = this.route.snapshot.queryParamMap.get('username');

    if (registered === '1' && this.mode === 'login') {
      this.successMessage = 'Account created. Please login with your credentials.';
      if (username) {
        this.form.username = username;
      }
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.clearLoadingGuard();
  }

  get pageTitle(): string {
    if (this.mode === 'register') {
      return 'Account Registration';
    }

    if (this.mode === 'change-credentials') {
      return 'Change Credentials';
    }

    return 'Login';
  }

  get showBackButton(): boolean {
    return this.mode !== 'login';
  }

  get isLoginMode(): boolean {
    return this.mode === 'login';
  }

  get isRegisterMode(): boolean {
    return this.mode === 'register';
  }

  get isMechanicDetailsStep(): boolean {
    return this.isRegisterMode && this.registrationStep === 'mechanic-details';
  }

  get showRegistrationAccountFields(): boolean {
    return !this.isMechanicDetailsStep;
  }

  get showNameField(): boolean {
    return this.mode !== 'login' && this.showRegistrationAccountFields;
  }

  get showRoleField(): boolean {
    return this.mode === 'register' && this.showRegistrationAccountFields;
  }

  get submitLabel(): string {
    if (this.mode === 'register') {
      if (this.form.role === 'mechanic' && this.registrationStep === 'account') {
        return 'Next';
      }

      return 'Register';
    }

    if (this.mode === 'change-credentials') {
      return 'Update';
    }

    return 'Login';
  }

  onBack(): void {
    if (this.mode === 'register' && this.registrationStep === 'mechanic-details') {
      this.registrationStep = 'account';
      this.errorMessage = '';
      this.syncUi();
      return;
    }

    this.router.navigate(['/login']);
  }

  goToRegister(): void {
    this.router.navigate(['/register']);
  }

  submit(): void {
    // Routes workflow by current page mode (login/register/change-credentials).
    this.errorMessage = '';
    this.successMessage = '';

    if (this.mode === 'register') {
      this.register();
      return;
    }

    this.startLoadingGuard();

    if (this.mode === 'change-credentials') {
      this.updateCredentials();
      return;
    }

    this.authService
      .login(this.form.username, this.form.password)
      .pipe(
        timeout(this.requestTimeoutMs),
        finalize(() => {
          this.stopLoadingGuard();
        })
      )
      .subscribe({
        next: (response) => {
          const role = this.normalizeRole(response.role);
          const username = (response.username ?? '').trim() || this.form.username.trim();
          const displayName = (response.name ?? '').trim() || username;
          this.persistUserSession(username, role);

          if (role === 'mechanic') {
            this.router.navigate(['/maintenance-reservations'], {
              queryParams: {
                mechanic: displayName
              }
            });
            return;
          }

          const ownerId = username;
          this.persistOwnerId(ownerId);
          this.router.navigate(['/vehicle-profiles'], {
            queryParams: { ownerId }
          });
        },
        error: (error: unknown) => {
          this.errorMessage = this.getApiError(error, 'Invalid username or password.');
          this.syncUi();
        }
      });
  }

  private register(): void {
    const accountValidationMessage = this.getAccountValidationMessage();
    if (this.form.role === 'mechanic' && this.registrationStep === 'account') {
      // Fast local validation first to avoid unnecessary network calls.
      if (accountValidationMessage) {
        this.errorMessage = accountValidationMessage;
        this.syncUi();
        return;
      }

      // Hard gate: backend validates account fields and DB uniqueness before moving to details step.
      this.startLoadingGuard();
      this.authService
        .validateRegistrationAccount({
          name: this.form.name.trim(),
          username: this.form.username.trim(),
          email: this.form.email.trim(),
          password: this.form.password,
          role: this.form.role
        })
        .pipe(
          timeout(this.requestTimeoutMs),
          finalize(() => {
            this.stopLoadingGuard();
          })
        )
        .subscribe({
          next: (validation) => {
            if (!validation.valid) {
              // Server returns ordered messages; surface the first actionable error to the user.
              this.errorMessage = validation.errors[0] ?? 'Account details are invalid.';
              this.syncUi();
              return;
            }

            this.registrationStep = 'mechanic-details';
            this.syncUi();
          },
          error: (error: unknown) => {
            this.errorMessage = this.getApiError(error, 'Unable to validate account fields from database.');
            this.syncUi();
          }
        });
      return;
    }

    if (accountValidationMessage) {
      this.errorMessage = accountValidationMessage;
      this.syncUi();
      return;
    }

    const mechanicDetailsValidationMessage = this.getMechanicDetailsValidationMessage();
    if (mechanicDetailsValidationMessage) {
      this.errorMessage = mechanicDetailsValidationMessage;
      this.syncUi();
      return;
    }

    this.startLoadingGuard();

    const payload: RegisterRequest = {
      name: this.form.name.trim(),
      username: this.form.username.trim(),
      email: this.form.email.trim(),
      password: this.form.password,
      role: this.form.role,
      mechanicPhone: this.form.role === 'mechanic' ? this.form.mechanicPhone.trim() : undefined,
      mechanicLocation: this.form.role === 'mechanic' ? this.form.mechanicLocation.trim() : undefined
    };

    this.authService
      .register(payload)
      .pipe(
        timeout(this.requestTimeoutMs),
        finalize(() => {
          this.stopLoadingGuard();
        })
      )
      .subscribe({
        next: () => {
          this.registrationStep = 'account';
          this.router.navigate(['/login'], {
            queryParams: { registered: '1', username: this.form.username }
          });
        },
        error: (error: unknown) => {
          this.errorMessage = this.getApiError(error, 'Registration failed.');
          this.syncUi();
        }
      });
  }

  onRoleChanged(): void {
    if (this.form.role !== 'mechanic' && this.registrationStep === 'mechanic-details') {
      this.registrationStep = 'account';
    }
  }

  private updateCredentials(): void {
    const payload: ChangeCredentialsRequest = {
      name: this.form.name,
      username: this.form.username,
      email: this.form.email,
      password: this.form.password
    };

    this.authService
      .updateCredentials(payload)
      .pipe(
        timeout(this.requestTimeoutMs),
        finalize(() => {
          this.stopLoadingGuard();
        })
      )
      .subscribe({
        next: () => {
          this.successMessage = 'Credentials updated successfully.';
        },
        error: (error: unknown) => {
          this.errorMessage = this.getApiError(error, 'Update failed.');
          this.syncUi();
        }
      });
  }

  private getApiError(error: unknown, fallbackMessage: string): string {
    if (error instanceof TimeoutError) {
      return 'Request timed out. Please try again.';
    }

    if (error instanceof HttpErrorResponse) {
      if (error.status === 0) {
        return `Cannot reach backend auth API at ${API_BASE_URL}.`;
      }

      if (typeof error.error === 'string' && error.error.trim().length > 0) {
        return error.error;
      }

      if (error.error?.message && typeof error.error.message === 'string') {
        return error.error.message;
      }

      if (error.error?.detail && typeof error.error.detail === 'string') {
        return error.error.detail;
      }

      if (error.error?.title && typeof error.error.title === 'string') {
        return error.error.title;
      }

      if (error.error?.errors && typeof error.error.errors === 'object') {
        const validationErrors = Object.values(error.error.errors)
          .flatMap((item: unknown) => (Array.isArray(item) ? item : [item]))
          .filter((item: unknown): item is string => typeof item === 'string' && item.trim().length > 0);

        if (validationErrors.length > 0) {
          return validationErrors[0];
        }
      }
    }

    return fallbackMessage;
  }

  private startLoadingGuard(): void {
    // Backup timer ensures loading is cleared even if callbacks never fire.
    this.clearLoadingGuard();
    this.loading = true;

    this.loadingGuardTimer = setTimeout(() => {
      this.ngZone.run(() => {
        if (!this.loading) {
          return;
        }

        this.loading = false;
        if (!this.errorMessage) {
          this.errorMessage = 'Request timed out. Please try again.';
        }

        this.syncUi();
      });
    }, this.requestTimeoutMs + 1000);

    this.syncUi();
  }

  private stopLoadingGuard(): void {
    this.clearLoadingGuard();
    this.loading = false;
    this.syncUi();
  }

  private persistOwnerId(ownerId: string): void {
    if (!ownerId) {
      return;
    }

    try {
      localStorage.setItem('autoInsight.ownerId', ownerId);
    } catch {
      // Ignore storage limitations in private browser contexts.
    }
  }

  private persistUserSession(username: string, role: UserRole): void {
    try {
      localStorage.setItem('autoInsight.username', username);
      localStorage.setItem('autoInsight.role', role);
    } catch {
      // Ignore storage limitations in private browser contexts.
    }
  }

  private normalizeRole(role: UserRole | string | null | undefined): UserRole {
    return role === 'mechanic' ? 'mechanic' : 'owner';
  }

  private getAccountValidationMessage(): string {
    const name = this.form.name.trim();
    if (!name) {
      return 'Name is required.';
    }
    if (name.length < 2 || name.length > 80) {
      return 'Name must be 2-80 characters.';
    }

    const usernameValidationMessage = this.getUsernameValidationMessage(this.form.username);
    if (usernameValidationMessage) {
      return usernameValidationMessage;
    }

    const email = this.form.email.trim();
    if (!email) {
      return 'Email is required.';
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return 'Enter a valid email address.';
    }

    if (!this.form.password) {
      return 'Password is required.';
    }
    if (this.form.password.length < 12) {
      return 'Password must be at least 12 characters.';
    }

    return '';
  }

  private getMechanicDetailsValidationMessage(): string {
    if (this.form.role !== 'mechanic') {
      return '';
    }

    const phone = this.form.mechanicPhone.trim();
    if (!phone) {
      return 'Mechanic contact is required.';
    }
    if (!/^[0-9()+\-.\s]{7,20}$/.test(phone)) {
      return 'Mechanic contact must be 7-20 characters and contain only digits or phone symbols.';
    }

    const location = this.form.mechanicLocation.trim();
    if (!location) {
      return 'Mechanic location is required.';
    }
    if (location.length < 2 || location.length > 120) {
      return 'Mechanic location must be 2-120 characters.';
    }

    return '';
  }

  private getUsernameValidationMessage(rawUsername: string): string {
    const username = rawUsername.trim();
    if (username.length < 3 || username.length > 30) {
      return 'Username must be 3-30 characters.';
    }

    if (!/^[a-zA-Z0-9._-]+$/.test(username)) {
      return 'Username can only include letters, numbers, period, underscore, and hyphen.';
    }

    if (/^[._-]|[._-]$/.test(username)) {
      return 'Username cannot start or end with period, underscore, or hyphen.';
    }

    return '';
  }

  private clearLoadingGuard(): void {
    if (this.loadingGuardTimer !== null) {
      clearTimeout(this.loadingGuardTimer);
      this.loadingGuardTimer = null;
    }
  }

  private syncUi(): void {
    if (this.destroyed) {
      return;
    }

    this.cdr.detectChanges();
  }
}
